import type { Column } from "@shared/types/data-table"
import type { Campaign, CampaignPerformance } from "@shared/types/engagement.types"
import type { ColumnManagementConfig } from "@shared/lib/tables/utils/column-management"
import {
  createStatusColumn,
  createCurrencyColumn,
  createDateColumn,
  createTextColumn,
  createTruncatedColumn,
  createCustomColumn,
} from "./column-factories"

// Usage example for CampaignTable:
// const columns = getCampaignColumns('all')
// <BaseEntityTable columns={columns} ... />

export const CAMPAIGN_COLUMNS: Record<string, Column<Campaign>> = {
  name: createTruncatedColumn<Campaign>(
    "name",
    "Name",
    200,
    (c) => c.name,
    "font-bold"
  ),
  status: createStatusColumn<Campaign>("campaign"),
  objective: createTextColumn<Campaign>(
    "objective" as keyof Campaign, "Objective", 140, undefined, undefined,
    (v: string) => v?.replace(/_/g, ' ') ?? ''
  ),
  rewardMechanic: createTextColumn<Campaign>(
    "rewardMechanic" as keyof Campaign, "Reward", 140, undefined, undefined,
    (v: string) => v?.replace(/_/g, ' ') ?? ''
  ),
  enrolledCount: createCustomColumn<Campaign>("enrolledCount" as keyof Campaign, "Enrolled", 100,
    (c) => <span className="text-sm tabular-nums">{c.enrolledCount.toLocaleString()}</span>
  ),
  revenue: createCurrencyColumn<Campaign>("revenue" as keyof Campaign, "Revenue"),
  roi: createCustomColumn<Campaign>("roi" as keyof Campaign, "ROI", 80,
    (c) => <span className="font-medium text-emerald-600 text-sm">{c.roi}x</span>
  ),
  budget: createCurrencyColumn<Campaign>("budget" as keyof Campaign, "Budget"),
  startDate: createDateColumn<Campaign>("startDate" as keyof Campaign, "Start", 130, false),
  endDate: createDateColumn<Campaign>("endDate" as keyof Campaign, "End", 130, false),
  createdAt: createDateColumn<Campaign>("createdAt" as keyof Campaign, "Created", 150),
}

export const CAMPAIGN_STATUS_CONFIG = {
  all: ["name", "status", "objective", "enrolledCount", "revenue", "roi", "startDate", "endDate"],
  active: ["name", "objective", "enrolledCount", "revenue", "roi", "endDate"],
  draft: ["name", "objective", "rewardMechanic", "budget", "createdAt"],
  completed: ["name", "objective", "enrolledCount", "revenue", "roi", "startDate", "endDate"],
} as const

export const getCampaignColumns = (status: string = 'all'): Column<Campaign>[] => {
  const keys = CAMPAIGN_STATUS_CONFIG[status as keyof typeof CAMPAIGN_STATUS_CONFIG] || CAMPAIGN_STATUS_CONFIG.all
  return keys.map(key => CAMPAIGN_COLUMNS[key]).filter(Boolean)
}

export const getCampaignColumnConfig = (status: string = 'all'): ColumnManagementConfig[] => {
  const visibleKeys: Set<string> = new Set(
    CAMPAIGN_STATUS_CONFIG[status as keyof typeof CAMPAIGN_STATUS_CONFIG] || CAMPAIGN_STATUS_CONFIG.all
  )
  return Object.entries(CAMPAIGN_COLUMNS).map(([key, col], index) => ({
    id: col.key,
    label: typeof col.header === 'string' ? col.header : col.key,
    visible: visibleKeys.has(key),
    required: ["name", "status"].includes(key),
    order: index,
  }))
}

// CampaignPerformance columns (for the analytics table)
// Usage: const columns = getCampaignPerformanceColumns()

export const CAMPAIGN_PERFORMANCE_COLUMNS: Record<string, Column<CampaignPerformance>> = {
  campaignName: createTruncatedColumn<CampaignPerformance>(
    "campaignName",
    "Campaign",
    200,
    (c) => c.campaignName,
    "font-medium"
  ),
  enrollments: {
    ...createCustomColumn<CampaignPerformance>("enrollments" as keyof CampaignPerformance, "Enrollments", 110,
      (c) => <span className="text-sm tabular-nums text-right block">{c.enrollments.toLocaleString()}</span>
    ),
    align: "right",
  },
  redemptions: {
    ...createCustomColumn<CampaignPerformance>("redemptions" as keyof CampaignPerformance, "Redemptions", 110,
      (c) => <span className="text-sm tabular-nums text-right block">{c.redemptions.toLocaleString()}</span>
    ),
    align: "right",
  },
  revenue: createCurrencyColumn<CampaignPerformance>("revenue" as keyof CampaignPerformance, "Revenue"),
  roi: createCustomColumn<CampaignPerformance>("roi" as keyof CampaignPerformance, "ROI", 80,
    (c) => <span className="font-medium text-emerald-600 text-sm">{c.roi}x</span>
  ),
  conversionRate: {
    ...createCustomColumn<CampaignPerformance>("conversionRate" as keyof CampaignPerformance, "Conversion", 100,
      (c) => <span className="text-sm tabular-nums text-right block">{(c.conversionRate * 100).toFixed(1)}%</span>
    ),
    align: "right",
  },
}

export const getCampaignPerformanceColumns = (): Column<CampaignPerformance>[] => {
  return Object.values(CAMPAIGN_PERFORMANCE_COLUMNS)
}
